import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { SavedAnalysis } from '../types/analytics';
import AnalysisChartRenderer from './AnalysisChartRenderer';
import { Transaction } from '../types';
import { FileSpreadsheet, FileText } from 'lucide-react';

interface AnalysisPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  analysis: SavedAnalysis | null;
  transactions: Transaction[];
  gradingOrders: any[];
  expenses?: any[];
  onChartTypeChange?: (analysis: SavedAnalysis, newChartType: string) => void;
  getValidChartTypes?: (analysis: SavedAnalysis) => string[];
  getAnalysisTypeIcon?: (chartType: string) => string;
  onExportData?: () => void;
  onExportPDF?: (chartElement: HTMLElement | null) => Promise<void>;
  onError?: (message: string) => void;
}

const AnalysisPreviewDialog: React.FC<AnalysisPreviewDialogProps> = ({
  open,
  onOpenChange,
  analysis,
  transactions,
  gradingOrders,
  expenses = [],
  onChartTypeChange,
  getValidChartTypes,
  getAnalysisTypeIcon,
  onExportData,
  onExportPDF,
  onError
}) => {
  if (!analysis) return null;

  const defaultGetIcon = (chartType: string) => {
    switch (chartType) {
      case 'bar': return '📊';
      case 'line': return '📈';
      case 'pie': return '🥧';
      case 'table': return '📋';
      case 'summary': return '📄';
      case 'stacked_bar': return '📊';
      case 'multiple_line': return '📈';
      default: return '📊';
    }
  };

  const iconFunction = getAnalysisTypeIcon || defaultGetIcon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{analysis.name}</DialogTitle>
          <DialogDescription>
            Preview of saved analysis
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex flex-wrap gap-2 items-center">
            {analysis.metrics && analysis.metrics.length > 0 && (
              <Badge variant="outline">
                Metrics: {analysis.metrics.length}
              </Badge>
            )}
            {analysis.groupBy && analysis.groupBy.length > 0 && (
              <Badge variant="outline">
                Group By: {analysis.groupBy.length}
              </Badge>
            )}
            {onExportData && (
              <button
                type="button"
                onClick={onExportData}
                className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-border bg-background hover:bg-muted/50 cursor-pointer"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Download Data
              </button>
            )}
            {onExportPDF && (
              <button
                type="button"
                onClick={async () => {
                  // Find the chart container element - use a timeout to ensure chart is rendered
                  await new Promise(resolve => setTimeout(resolve, 100));
                  const chartContainer = document.querySelector('[data-chart-container]') as HTMLElement;
                  if (chartContainer) {
                    await onExportPDF(chartContainer);
                  } else {
                    onError?.('Chart not available. Please try again.');
                  }
                }}
                className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-border bg-background hover:bg-muted/50 cursor-pointer"
              >
                <FileText className="h-4 w-4" />
                Download PDF
              </button>
            )}
            {onChartTypeChange && getValidChartTypes && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-border bg-transparent hover:bg-muted/50 cursor-pointer"
                  >
                    {iconFunction(analysis.chartType || 'bar')} {(analysis.chartType || 'bar').replace('_', ' ').toUpperCase()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[200px]">
                  <DropdownMenuLabel>Change Chart Type</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {getValidChartTypes(analysis).map((chartType) => (
                    <DropdownMenuItem
                      key={chartType}
                      onClick={() => {
                        if (chartType !== analysis.chartType) {
                          onChartTypeChange(analysis, chartType);
                        }
                      }}
                      className={chartType === analysis.chartType ? 'bg-muted' : ''}
                    >
                      <span className="mr-2">{iconFunction(chartType)}</span>
                      {chartType === analysis.chartType && '✓ '}
                      {chartType.replace('_', ' ').toUpperCase()}
                      {chartType === analysis.chartType && ' (current)'}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="border rounded-lg p-4 bg-background" data-chart-container>
            <AnalysisChartRenderer
              analysis={analysis}
              transactions={transactions}
              gradingOrders={gradingOrders}
              height={400}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AnalysisPreviewDialog; 